import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Card, CardContent, Typography } from '@mui/material';
import { CuitProfileForm } from '@/components/CuitProfileForm';
import * as cuitProfilesApi from '@/api/cuitProfiles';
import { toApiError } from '@/api/client';
import type { AfipEnvironment, CuitProfileIvaCondition } from '@/api/types';
import { AFIP_ENVIRONMENT_LABEL } from '@/lib/labels';

/**
 * Prefill por search params. Lo usa el atajo "registrar en el otro ambiente" del detalle: como
 * el `Environment` de un CuitProfile es inmutable, operar en Producción tras testear en
 * Homologación requiere registrar el MISMO CUIT como un profile nuevo en el otro entorno. El
 * detalle navega acá con los datos fiscales precargados y el entorno opuesto.
 */
export interface NewCuitSearch {
  cuit?: string;
  razonSocial?: string;
  condicionIva?: CuitProfileIvaCondition;
  street?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  ingresosBrutos?: string;
  fechaInicioActividades?: string;
  environment?: AfipEnvironment;
}

const IVA_VALUES: CuitProfileIvaCondition[] = [
  'ResponsableInscripto',
  'Monotributista',
  'Exento',
  'ConsumidorFinal',
  'NoResponsable',
  'SujetoNoCategorizado',
];

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

export const Route = createFileRoute('/_authenticated/cuit-profiles/new')({
  validateSearch: (search: Record<string, unknown>): NewCuitSearch => ({
    cuit: str(search.cuit),
    razonSocial: str(search.razonSocial),
    condicionIva: IVA_VALUES.includes(search.condicionIva as CuitProfileIvaCondition)
      ? (search.condicionIva as CuitProfileIvaCondition)
      : undefined,
    street: str(search.street),
    city: str(search.city),
    province: str(search.province),
    postalCode: str(search.postalCode),
    ingresosBrutos: str(search.ingresosBrutos),
    fechaInicioActividades: str(search.fechaInicioActividades),
    environment:
      search.environment === 'Homologation' || search.environment === 'Production'
        ? search.environment
        : undefined,
  }),
  component: NewCuitProfilePage,
});

function NewCuitProfilePage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isTwin = Boolean(search.cuit && search.environment);

  const mutation = useMutation({
    mutationFn: cuitProfilesApi.registerCuitProfile,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['cuit-profiles'] });
      navigate({ to: '/cuit-profiles/$id', params: { id: created.id } });
    },
    onError: (err) => {
      setErrorMessage(toApiError(err).message);
    },
  });

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Nuevo CUIT
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Registrá los datos fiscales del contribuyente. Después de crearlo vas a poder subir el
        certificado AFIP.
      </Typography>

      {isTwin && search.environment ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Estás registrando este CUIT en <strong>{AFIP_ENVIRONMENT_LABEL[search.environment]}</strong>.
          Es un perfil independiente del de otro entorno y necesita su propio certificado AFIP de{' '}
          {AFIP_ENVIRONMENT_LABEL[search.environment].toLowerCase()}.
        </Alert>
      ) : null}

      <Card>
        <CardContent>
          <CuitProfileForm
            defaultValues={{
              cuit: search.cuit,
              razonSocial: search.razonSocial,
              condicionIva: search.condicionIva,
              street: search.street,
              city: search.city,
              province: search.province,
              postalCode: search.postalCode,
              ingresosBrutos: search.ingresosBrutos,
              fechaInicioActividades: search.fechaInicioActividades,
              environment: search.environment,
            }}
            errorMessage={errorMessage}
            submitting={mutation.isPending}
            onSubmit={(values) => {
              setErrorMessage(null);
              mutation.mutate(values);
            }}
            onCancel={() => navigate({ to: '/cuit-profiles' })}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
